import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import s from './Subscription.module.css';
import IconPricingFeature from '../../../../shared/ui/icons/IconPricingFeature';
import UpgradeButton from './UpgradeButton';
import { RootState, useAppDispatch } from '../../../../store/store';
import {
    fetchSubscriptionProducts,
    fetchUserSubscription,
} from '../../../../store/slices/subscriptionSlice';
import IconSubscriptionCalendar from '../../../../shared/ui/icons/IconSubscriptionCalendar';
import IconSubscriptionClock from '../../../../shared/ui/icons/IconSubscriptionClock';
import { differenceInDays, format } from 'date-fns';
import Loader from '../../../../shared/ui/loader/Loader';

const Subscription = () => {
    const dispatch = useAppDispatch();
    const { products, isLoading, error, userSubscription } = useSelector(
        (state: RootState) => state.subscription,
    );

    const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
    const [autoRenew, setAutoRenew] = useState<boolean>(true);

    useEffect(() => {
        if (!products || products.length === 0) {
            dispatch(fetchSubscriptionProducts());
        }
        if (!userSubscription) {
            dispatch(fetchUserSubscription());
        }
    }, [dispatch, products, userSubscription]);

    useEffect(() => {
        if (userSubscription && products.length > 0) {
            const found = products.find(
                (p) =>
                    (p.attributes.metadata?.plan || '').toLowerCase() ===
                    userSubscription.plan?.toLowerCase(),
            );
            if (found) {
                setSelectedProduct(found);
            }
            setAutoRenew(userSubscription.auto_renew ?? true);
        }
    }, [userSubscription, products]);

    const subscriptionDateRaw = userSubscription?.subscription_ends_at || null;
    const subscriptionDate = subscriptionDateRaw
        ? format(new Date(subscriptionDateRaw), 'do MMMM yyyy')
        : 'N/A';

    const daysUntilNext = subscriptionDateRaw
        ? Math.max(0, differenceInDays(new Date(subscriptionDateRaw), new Date()))
        : null;

    const planLabel =
        userSubscription?.plan && typeof userSubscription.plan === 'string'
            ? userSubscription.plan.charAt(0).toUpperCase() + userSubscription.plan.slice(1)
            : 'No';

    return (
        <div className={s.wrapper}>
            <div className={s.currentPlan}>
                <div className={s.mainTitle}>
                    <h2>Your current plan</h2>
                    <div>
                        <span className={s.switchTitle}>Renew automatically</span>
                        <label className={s.switch}>
                            <input
                                type="checkbox"
                                checked={autoRenew}
                                onChange={(e) => setAutoRenew(e.target.checked)}
                            />
                            <span className={s.slider}></span>
                        </label>
                    </div>
                </div>

                <div className={s.subHeader}>
                    <div>
                        <p className={s.planName}>{planLabel} Plan</p>
                        <p className={s.planNote}>Upgrade anytime to unlock advanced resources</p>
                        <div className={s.actions}>
                            <UpgradeButton product={selectedProduct} autoRenew={autoRenew} />
                            <button className={s.supportBtn}>Contact support</button>
                        </div>
                    </div>

                    <div className={s.subscriptionOverview}>
                        <h3 className={s.subscriptionTitle}>Subscription overview</h3>

                        <div className={s.subscriptionRow}>
                            <span className={s.subscriptionIcon}>
                                <IconSubscriptionCalendar />
                            </span>
                            <span className={s.subscriptionLabel}>Subscription date:</span>
                            <span className={s.subscriptionValue}>{subscriptionDate}</span>
                        </div>

                        <div className={s.subscriptionRow}>
                            <span className={s.subscriptionIcon}>
                                <IconSubscriptionClock />
                            </span>
                            <span className={s.subscriptionLabel}>
                                Days until next subscription:
                            </span>
                            <span className={s.subscriptionValue}>
                                {daysUntilNext !== null ? daysUntilNext : 'N/A'}
                            </span>
                        </div>

                        {userSubscription?.is_on_trial && (
                            <div className={s.subscriptionRow}>
                                <span className={s.subscriptionIcon}>
                                    <IconSubscriptionClock />
                                </span>
                                <span className={s.subscriptionLabel}>Trial ends at:</span>
                                <span className={s.subscriptionValue}>
                                    {userSubscription.trial_ends_at
                                        ? format(
                                              new Date(userSubscription.trial_ends_at),
                                              'do MMMM yyyy',
                                          )
                                        : 'N/A'}
                                </span>
                            </div>
                        )}

                        {userSubscription?.is_trial_expired && !userSubscription.is_on_trial && (
                            <div className={`${s.subscriptionRow} ${s.warningRow}`}>
                                <span className={s.subscriptionLabel}>Trial expired:</span>
                                <span className={s.subscriptionValue}>
                                    Please upgrade to continue using SimpleeCreate.
                                </span>
                            </div>
                        )}

                        {userSubscription?.is_payment_failed && (
                            <div className={`${s.subscriptionRow} ${s.errorRow}`}>
                                <span className={s.subscriptionLabel}>Payment failed:</span>
                                <span className={s.subscriptionValue}>
                                    We couldn’t process your last payment. Please update your
                                    billing details.
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={s.pricing}>
                <h3>Pricing plans</h3>

                {isLoading && <Loader />}
                {error && <p className={s.error}>{error}</p>}

                <div className={s.cards}>
                    {[...products]
                        .filter((p) => p.attributes.recurring?.interval === 'month')
                        .sort((a, b) => {
                            const orderA = parseInt(a.attributes.metadata?.tier || '999', 10);
                            const orderB = parseInt(b.attributes.metadata?.tier || '999', 10);
                            return orderA - orderB;
                        })
                        .map((p) => {
                            const planKey = (p.attributes.metadata?.plan || '').toLowerCase();
                            const isActive =
                                selectedProduct &&
                                (selectedProduct.attributes.metadata?.plan || '').toLowerCase() ===
                                    planKey;

                            const price =
                                p.attributes.unit_amount > 0
                                    ? `$${p.attributes.unit_amount}`
                                    : 'Free';
                            const subPrice = `/ ${p.attributes.recurring.interval}`;

                            const rawPlan = p.attributes.metadata?.plan ?? p.attributes.name;
                            const planLabel = rawPlan.charAt(0).toUpperCase() + rawPlan.slice(1);

                            return (
                                <div
                                    key={p.id}
                                    className={`${s.card} ${isActive ? s.activeCard : ''}`}
                                    onClick={() => setSelectedProduct(p)}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <h4>{planLabel}</h4>
                                    <p className={s.subtext}>{p.attributes.description}</p>

                                    <ul>
                                        {(p.attributes.features || []).map((f, idx) => (
                                            <li key={idx} className={s.featureItem}>
                                                <span>
                                                    <IconPricingFeature />
                                                </span>
                                                {f}
                                            </li>
                                        ))}
                                    </ul>

                                    <p className={s.price}>
                                        {price} <span className={s.subPrice}>{subPrice}</span>
                                    </p>
                                </div>
                            );
                        })}
                </div>
            </div>
        </div>
    );
};

export default Subscription;
